import { EDITOR_SHAPE_TEMP_ID_PREFIX } from '../constants/canvas';
import { API_GATEWAY_URL } from '../constants/config';
import { DetectShapesJobStatus, ShapeKeyType, } from '../interfaces/shape';
import { millimeterToMeter } from '../utils/math';
import { getCylinderBoundingBox } from '../utils/three';
import { GET_INSPECTION_AREA_API_URL } from './inspectionArea';
import { withStandardHandlers } from './utils';
export const GET_SHAPES_API_URL = (projectId, inspectionAreaId) => `${GET_INSPECTION_AREA_API_URL(projectId, inspectionAreaId)}/shapes`;
export const DELETE_SHAPES_API_URL = (projectId, inspectionAreaId) => `${GET_INSPECTION_AREA_API_URL(projectId, inspectionAreaId)}/shapes:batchDelete`;
export const DETECT_SHAPES_API_URL = `${API_GATEWAY_URL}/detect-shapes`;
export const JOB_STATUS_API_URL = (jobToken) => `${API_GATEWAY_URL}/job-status?token=${jobToken}`;
/**
 * Get the internal shape parameters for the shape.
 *
 * @param diameter Diameter of the shape
 * @param shapeKey Shape key
 */
export const getInternalShapeParameters = (diameter, shapeKey) => {
    if (shapeKey === ShapeKeyType.CYLINDER) {
        return { diameter: millimeterToMeter(diameter) };
    }
    return null;
};
/**
 * Get shapes for the inspection area.
 *
 * @param accessToken Token
 * @param projectId Project ID
 * @param inspectionAreaId InspectionArea ID
 *
 * @return Shapes object
 */
export const getShapes = (accessToken, projectId, inspectionAreaId) => {
    const fn = () => fetch(GET_SHAPES_API_URL(projectId, inspectionAreaId), {
        headers: {
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.shapes.get')().then((shapes) => {
        // Create bbox for each shapes
        shapes.cylinders?.forEach((cylinder) => {
            const bbox = getCylinderBoundingBox(cylinder);
            cylinder.bbox = [bbox.min.toArray(), bbox.max.toArray()];
        });
        return shapes;
    });
};
/**
 * Delete shapes for the inspection area.
 *
 * @param {string} accessToken token
 * @param {string} projectId project id
 * @param {string} inspectionAreaId inspectionArea id
 * @param {ShapesId} shapesId all ids of cylinders, tori, planes
 * @return {Boolean} success or failure
 */
export const deleteShapes = (accessToken, projectId, inspectionAreaId, shapesId) => {
    const filteredShapesId = {
        cylinders: shapesId.cylinders.filter((id) => id && !id.startsWith(EDITOR_SHAPE_TEMP_ID_PREFIX)),
        polygons: shapesId.polygons.filter((id) => id && !id.startsWith(EDITOR_SHAPE_TEMP_ID_PREFIX)),
    };
    // No need to send request if deleting shapes are not saved yet (temporarily detected)
    if (!filteredShapesId.cylinders.length && !filteredShapesId.polygons.length) {
        return Promise.resolve(true);
    }
    const fn = () => fetch(DELETE_SHAPES_API_URL(projectId, inspectionAreaId), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(filteredShapesId),
    });
    return withStandardHandlers(fn, 'errors.shapes.delete')().then((response) => response || true);
};
/**
 * Detect shapes based on the anchors and mask regions.
 *
 * @param {string} accessToken token
 * @param {string} projectId project ID
 * @param {string} inspectionAreaId inspection area ID
 * @param {string} inspectionSheetId inspection sheet ID
 * @param {string} shape_type cylinders, tori, planes
 * @param {Anchors} anchors all points with diameter
 * @param {MaskRegion[]} maskRegions mask regions
 * @return {string} job's token - used to retrieve job's status
 */
export const detectShapes = (accessToken, projectId, inspectionAreaId, inspectionSheetId, shape_type, anchors, maskRegions) => {
    const fn = () => fetch(`${DETECT_SHAPES_API_URL}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            project_id: projectId,
            inspection_area_id: inspectionAreaId,
            inspection_sheet_id: inspectionSheetId,
            shape_type,
            parameters_for_detecting_shapes: anchors[shape_type].map((anchor) => ({
                internal_shape_parameters: getInternalShapeParameters(anchor.diameter || 0, shape_type),
                positions: anchor.points.map((point) => ({ x: point[0], y: point[1], z: point[2] })),
                plane_side: anchor.plane_side,
                is_virtual: anchor.is_virtual,
            })),
            mask_region_ids: maskRegions.map((maskRegion) => maskRegion.region_id),
        }),
    });
    return withStandardHandlers(fn, 'errors.shapes.detect')().then((response) => response.job_token);
};
/**
 * Poll the status of a `detect-shapes` job (identified by the `job_token` from `detectShapes`).
 *
 * Ported from the Editor's `checkJobStatusForDetectShapes`, dropping its axios / error-modal
 * coupling for the `fetch` + `withStandardHandlers` style of this module. `withStandardHandlers`
 * would normally unwrap a `results` field, but here the caller needs `job_status` too, so
 * unwrapping is disabled and the full envelope is read directly.
 *
 * @param {string} accessToken token
 * @param {string} jobToken job's token — from the detect-shapes API
 * @return {DetectShapesJobResult | null} the detection result once the job settles (an empty
 *   `list_shapes` when it FAILED), or `null` while the job is still running (keep polling).
 */
export const checkJobStatusForDetectShapes = async (accessToken, jobToken) => {
    const fn = () => fetch(JOB_STATUS_API_URL(jobToken), {
        headers: {
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    const response = await withStandardHandlers(fn, 'errors.shapes.detect', false)();
    if (response.job_status === DetectShapesJobStatus.SUCCEEDED)
        return response.results;
    if (response.job_status === DetectShapesJobStatus.FAILED)
        return { list_shapes: [] };
    return null;
};
